/**
 * The dashboard inventory, transcribed from §9.6 of `docs/V1_BRAINSTORM.md`
 * (“GM prototype — dictated screen map”, 2026-10-16).
 *
 * Why this is data and not markup: the GM dictated the *map* — which groups exist, which
 * tiles belong in them, whether a tile is ours or a vendor deep link, and where its numbers
 * come from. Keeping that in one table means a tile can be added, moved or re-sourced in a
 * single place, and the smoke suite can check the dashboard against the document instead of
 * against a developer's memory (D-45).
 *
 * Every tile here is a **placeholder** today: nothing beyond the shell is built yet. They
 * are rendered honestly — a deep link names its destination and says the portal URL is not
 * configured, an in-app tile says “planned”, and the deferred one quotes the decision that
 * deferred it (§9.4: never a silently empty screen).
 */
import type { ComponentType } from 'react'
import {
  Award,
  BarChart3,
  BookOpen,
  CalendarClock,
  FileText,
  FolderOpen,
  Gauge,
  GraduationCap,
  Grid3x3,
  Mail,
  Package,
  PieChart,
  RefreshCw,
  ShoppingCart,
  Star,
  TrendingUp,
  Truck,
  Users,
} from 'lucide-react'

export type TileKind = 'deep-link' | 'in-app'
export type TileStatus = 'live' | 'placeholder' | 'deferred'

export interface DashboardTile {
  key: string
  label: string
  kind: TileKind
  status: TileStatus
  /** One line from the dictated map: what the tab shows or does. */
  description: string
  /** The production source once real data is wired (§9.6 “production source”). */
  source: string
  /** Deep links only: the system the button would open. */
  opensIn?: string
  /** The vendor portal, once it is mapped — opened in a new tab (D-47). */
  url?: string
  /** For our own screens: the in-app route this tile opens. */
  route?: string
  /** Third-party dashboard embedded on that route (MobieTrain Insights → Luzmo). */
  embed?: { provider: string; env: string; note?: string }
  /** Still-open question or decision that blocks real data. */
  blockedBy?: string
  /** Capability required to see/open this tile (backend catalogue key). */
  permission?: string
  /** Hide the availability badge on this tile (used for the live 4-Wall tab). */
  hideBadge?: boolean
  icon: ComponentType<{ className?: string }>
  accent: string
}

export interface TileGroupSpec {
  key: string
  title: string
  blurb: string
  /** Capability required to see this category at all. */
  permission?: string
  tiles: DashboardTile[]
}

/** “Insights in, links out” (§1, §9.4): vendor systems are deep links, never rebuilt. */
export const MODULE_TILES: TileGroupSpec = {
  key: 'modules',
  title: 'Modules',
  blurb: 'The vendor systems we read from — each one opens in its own portal.',
  permission: 'modules.view',
  tiles: [
    {
      key: 'horeko',
      label: 'Horeko',
      kind: 'deep-link',
      status: 'placeholder',
      description: 'Recipes, stock and checklists.',
      source: 'Horeko — prep amounts, recipe/BOM, stock, cost prices',
      opensIn: 'Horeko',
      url: 'https://app.horeko.com/',
      permission: 'modules.horeko',
      icon: BookOpen,
      accent: 'from-salsa-500 to-salsa-700',
    },
    {
      key: 'basecone',
      label: 'Basecone',
      kind: 'deep-link',
      status: 'placeholder',
      description: 'Invoice queue and approvals.',
      source: 'Basecone — invoice lines and GL coding',
      opensIn: 'Basecone',
      url: 'https://secure.basecone.com/frontend/',
      permission: 'modules.basecone',
      icon: FileText,
      accent: 'from-sky-500 to-indigo-600',
    },
    {
      key: 'shiftbase',
      label: 'Shiftbase',
      kind: 'deep-link',
      status: 'placeholder',
      description: 'Roster, hours and leave.',
      source: 'Shiftbase — who is on now, planned vs actual, grid hours',
      opensIn: 'Shiftbase',
      url: 'https://app.shiftbase.com/schedule/',
      permission: 'modules.shiftbase',
      icon: CalendarClock,
      accent: 'from-indigo-500 to-violet-600',
    },
    {
      key: 'connexie',
      label: 'Connexie',
      kind: 'deep-link',
      status: 'placeholder',
      description: 'Contracts, payslips and HR.',
      source: 'Connexie — contract hours and hourly cost rates',
      opensIn: 'Connexie',
      url: 'https://web.connexie.net/?start=1',
      permission: 'modules.connexie',
      icon: Users,
      accent: 'from-teal-500 to-emerald-600',
    },
    {
      key: 'butlaroo',
      label: 'Butlaroo',
      kind: 'deep-link',
      status: 'placeholder',
      description: 'Orders, payments and Z-reports.',
      source: 'Butlaroo — order lines, tender mix, Z-reports (Adyen settles)',
      opensIn: 'Butlaroo',
      url: 'https://dashboard.butlaroo.com/',
      permission: 'modules.butlaroo',
      icon: ShoppingCart,
      accent: 'from-mango-500 to-salsa-600',
    },
    {
      key: 'training',
      label: 'Training',
      kind: 'deep-link',
      status: 'placeholder',
      description: 'Your own training — courses, progress, certificates.',
      source: 'MobieTrain — deep link (D-23/D-25: your training, not the team’s)',
      opensIn: 'MobieTrain',
      permission: 'training.own',
      icon: GraduationCap,
      accent: 'from-fuchsia-500 to-pink-600',
    },
    {
      key: 'email',
      label: 'Email',
      kind: 'deep-link',
      status: 'placeholder',
      description: 'The store mailbox — Outlook on the web.',
      source: 'Microsoft 365 · Outlook (Q-35 answered: Outlook on the web)',
      opensIn: 'Outlook',
      url: 'https://outlook.cloud.microsoft/mail/',
      permission: 'modules.email',
      icon: Mail,
      accent: 'from-slate-500 to-slate-700',
    },
    {
      key: 'sharepoint',
      label: 'SharePoint',
      kind: 'deep-link',
      status: 'placeholder',
      description: 'Store documents, team sites and shared files.',
      source: 'Microsoft 365 — SharePoint document libraries and team sites',
      opensIn: 'SharePoint',
      url: 'https://www.microsoft365.com/launch/sharepoint',
      permission: 'modules.sharepoint',
      icon: FolderOpen,
      accent: 'from-cyan-500 to-blue-600',
    },
  ],
}

/** Four supplier portals — later Store OS generates the order sheet itself (M7). */
export const SUPPLIER_TILES: TileGroupSpec = {
  key: 'suppliers',
  title: 'Suppliers',
  blurb: 'Ordering and service portals. Store OS will generate the order sheet later (M7).',
  permission: 'suppliers.view',
  tiles: [
    {
      key: 'van_gelder',
      label: 'Van Gelder',
      kind: 'deep-link',
      status: 'placeholder',
      description: 'Supplier portal — later: order sheet generated by Store OS.',
      source: 'Van Gelder ordering',
      opensIn: 'the Van Gelder portal',
      url: 'https://www.vangeldernederland.nl/en_US/',
      icon: Truck,
      accent: 'from-orange-500 to-amber-600',
    },
    {
      key: 'bidfood',
      label: 'Bidfood',
      kind: 'deep-link',
      status: 'placeholder',
      description: 'Supplier portal.',
      source: 'Bidfood ordering',
      opensIn: 'the Bidfood portal',
      // The mapped link carried a `_requestid`, which is session-specific and would rot —
      // the clean webshop entry point survives (D-47).
      url: 'https://www.bidfood.nl/webshop/',
      icon: Truck,
      accent: 'from-lime-500 to-green-600',
    },
    {
      key: 'ftg',
      label: 'FTG',
      kind: 'deep-link',
      status: 'placeholder',
      description: 'Supplier portal.',
      source: 'FTG ordering',
      opensIn: 'the FTG portal',
      url: 'https://webshop.facilitytradegroup.nl/nl/companywishlist/',
      icon: Truck,
      accent: 'from-cyan-500 to-sky-600',
    },
    {
      key: 'rhima',
      label: 'Rhima',
      kind: 'deep-link',
      status: 'placeholder',
      description: 'Supplier portal and service desk.',
      source: 'Rhima — equipment and service calls (Q-05)',
      opensIn: 'the Rhima portal',
      icon: RefreshCw,
      accent: 'from-rose-500 to-red-600',
    },
  ],
}

/**
 * Insights — **our own builds** (D-22), not vendor screens. They are the reason the
 * integrations exist, which is why each one names the feeds it needs and what finance
 * still has to sign off.
 */
export const INSIGHT_TILES: TileGroupSpec = {
  key: 'insights',
  title: 'Insights',
  blurb: 'Built by Store OS from scratch (D-22) — the analysis layer is the product.',
  permission: 'insights.view',
  tiles: [
    {
      key: 'four_wall',
      label: '4-Wall Analysis',
      kind: 'in-app',
      status: 'placeholder',
      description: 'COL & COS - analyzed',
      source: 'Butlaroo · Horeko · Shiftbase · Connexie · Basecone + budgets (B18)',
      route: '/insights/four-wall-analysis',
      permission: 'insights.four_wall',
      hideBadge: true,
      icon: PieChart,
      accent: 'from-salsa-500 to-mango-600',
    },
    {
      key: 'grid_viewer',
      label: 'Grid Viewer',
      kind: 'in-app',
      status: 'live',
      description: 'The COL grid for one shop — hours per day, week or year.',
      source: 'COL Scenarios (Labor Grid) · store AOVs and blueprint transitions',
      route: '/insights/grid-viewer',
      permission: 'insights.grid_viewer',
      icon: Grid3x3,
      accent: 'from-emerald-500 to-teal-700',
    },
    {
      key: 'sales_analysis',
      label: 'Sales Analysis',
      kind: 'in-app',
      status: 'live',
      description: 'Sales, orders, AOV and refunds by hour, day, week, month, quarter or day of week.',
      source: 'Butlaroo paid orders + order lines',
      route: '/insights/sales-analysis',
      permission: 'insights.sales_analysis',
      hideBadge: true,
      icon: BarChart3,
      accent: 'from-sky-500 to-blue-700',
    },
    {
      key: 'product_analysis',
      label: 'Product Overview',
      kind: 'in-app',
      status: 'live',
      description: 'Product, fee and modifier mix from paid Butlaroo order lines.',
      source: 'Butlaroo paid orders + order lines',
      route: '/insights/product-overview',
      permission: 'insights.product_overview',
      hideBadge: true,
      icon: Package,
      accent: 'from-amber-500 to-orange-700',
    },
    {
      key: 'forecast',
      label: 'Forecast',
      kind: 'in-app',
      status: 'placeholder',
      description: 'Week/day forecast vs actual, accuracy, expected covers, hour curve.',
      source: 'Butlaroo history + HQ forecast (Q-29)',
      permission: 'insights.forecast',
      icon: TrendingUp,
      accent: 'from-emerald-500 to-teal-700',
    },
    {
      key: 'ratings',
      label: 'Ratings',
      kind: 'in-app',
      status: 'placeholder',
      description: 'Guest feedback from Google, Thuisbezorgd and Uber Eats in one place.',
      source: 'Google Business Profile · Thuisbezorgd · Uber Eats (Q-04)',
      permission: 'insights.ratings',
      icon: Star,
      accent: 'from-amber-500 to-orange-600',
    },
    {
      key: 'mobietrain_insights',
      label: 'MobieTrain Insights',
      kind: 'in-app',
      status: 'placeholder',
      description: 'Who has completed their training and who has not, completion %, expiring certificates.',
      source: 'MobieTrain Insights via Luzmo — embedded (D-47)',
      permission: 'insights.mobietrain',
      icon: Award,
      accent: 'from-fuchsia-500 to-purple-700',
    },
  ],
}

/** Tools — also ours (D-22): the jobs that keep other people's numbers honest. */
export const TOOL_TILES: TileGroupSpec = {
  key: 'tools',
  title: 'Tools',
  blurb: 'Our own tools. Each one closes a specific integration gap.',
  permission: 'tools.view',
  tiles: [
    {
      key: 'monthly_pl',
      label: 'Monthly P&L',
      kind: 'in-app',
      status: 'placeholder',
      description: 'Monthly profit & loss at store level.',
      source: 'Accounting package (Q-02) · Basecone · 4-wall model',
      icon: Gauge,
      accent: 'from-slate-600 to-slate-800',
    },
  ],
}

/** The dashboard, in the current category order (D-69). */
export const TILE_GROUPS: TileGroupSpec[] = [
  INSIGHT_TILES,
  SUPPLIER_TILES,
  MODULE_TILES,
  TOOL_TILES,
]

/** Every dictated tab, flattened — used by the dashboard counters and the smoke suite. */
export const ALL_TILES: DashboardTile[] = TILE_GROUPS.flatMap((group) => group.tiles)


